import 'vuetify/styles'
import {watch} from 'vue'
import {createVuetify, type ThemeDefinition} from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import {aliases, mdi} from 'vuetify/iconsets/mdi-svg'
import {en, fr} from 'vuetify/locale'
import {getActiveLocale} from '../utils/activeLocale'
// TODO: VCalendar requires vuetify labs package - temporarily disabled
// import { VCalendar } from 'vuetify/labs/VCalendar'

export interface VuetifyPluginOptions {
    lightTheme?: Partial<ThemeDefinition['colors']>
    darkTheme?: Partial<ThemeDefinition['colors']>
    defaultTheme?: 'light' | 'dark'
}

const defaultLightTheme: ThemeDefinition['colors'] = {
    primary: '#884aff',
    secondary: '#F0A868',
    accent: '#64CCC5',
    // Status colours are read as text on white (chips, tonal alerts, inline states), so each is
    // dark enough for 4.5:1 there; tonal backgrounds stay pale because they are this colour at a
    // low opacity. Validated by the owner on 2026-10-04 (docs/plans/billing-ui.md U20).
    error: '#B42318',
    info: '#1F5FAF',
    success: '#1E7A34',
    warning: '#9A4D00',
    background: '#FFFFFF',
    surface: '#FFFFFF',
    'on-background': '#2E3440',
    'on-surface': '#2E3440',
}

const defaultDarkTheme: ThemeDefinition['colors'] = {
    primary: '#9D88F3',
    secondary: '#FFB885',
    accent: '#7DDDD6',
    error: '#FF8A8A',
    info: '#90C3FF',
    success: '#66BB6A',
    warning: '#FFD082',
    // --- Dark theme background options ---
    // Bleu original
    background: '#1A1F2E',
    surface: '#242938',
    // Material Design (standard)
    // background: '#121212',
    // surface: '#1E1E1E',
    // OLED / Noir pur
    // background: '#000000',
    // surface: '#121212',
    // Anthracite doux (recommandé)
    // background: '#161616',
    // surface: '#1F1F1F',
    // Charbon chaud
    // background: '#141210',
    // surface: '#1C1A18',
    // Graphite
    // background: '#1A1A1A',
    // surface: '#252525',
    // --- Fin des options ---
    'on-background': '#E5E9F0',
    'on-surface': '#E5E9F0',
}

export function createVuetifyPlugin(options: VuetifyPluginOptions = {}) {
    const {lightTheme, darkTheme, defaultTheme = 'light'} = options

    const vuetify = createVuetify({
        // `mobile` must mean the phone tier and nothing else, so that JS and the
        // `phone` SCSS mixin flip at the same width. Vuetify's default is `lg`
        // (1145px here), which made every data table stack and every drawer go
        // temporary on a laptop. `sm` is 600px — exactly $bp-phone.
        // `md` is moved from Material 3's 840 to 960 — exactly $bp-tablet — so that
        // `mdAndUp` and `smAndDown` flip where `desktop-up` and `tablet-down` do. Vuetify
        // deep-merges this into its defaults: the other thresholds stay as they are. Its CSS
        // classes (`d-md-*`, `v-col` `md`) read `$grid-breakpoints`, moved the same way in
        // styles/settings.scss. See docs/plans/mobile-navigation.md (MN2).
        display: {
            mobileBreakpoint: 'sm',
            thresholds: {
                md: 960,
            },
        },
        // Without this Vuetify keeps its built-in English strings, so a French
        // screen still read « Items per page » and « 1-2 of 2 » under every
        // data table. The active locale comes from the same ref the i18n plugin
        // and the locale store already keep in sync, so the two cannot drift.
        locale: {
            locale: getActiveLocale(),
            fallback: 'en',
            messages: {en, fr},
        },
        components: {
            ...components,
            // VCalendar, // TODO: Re-enable when vuetify labs is installed
        },
        directives,
        icons: {
            defaultSet: 'mdi',
            aliases,
            sets: {
                mdi,
            },
        },
        defaults: {
            VSwitch: {
                inset: true,
            },
            VListItem: {
                slim: true,
            },
            VCard: {
                variant: "outlined"
            },
            VTextField: {
                variant: "solo-filled",
                density: "comfortable",
                flat: true,
            },
            VTextarea: {
                variant: "solo-filled",
                density: "comfortable",
                flat: true,
            },
            // A picker's input filters a list of ours, so the browser's own suggestions only
            // cover it. "suppress" sets autocomplete="off" and makes a named input's name unique,
            // which is what keeps Chrome's autofill from recognising the field.
            VAutocomplete: {
                autocomplete: "suppress",
            },
            VCombobox: {
                autocomplete: "suppress",
            },
            // A row of tabs wider than the screen shows its arrows on a phone too, where Vuetify
            // hides them by default and nothing else says the row scrolls (styles/_scroll-arrows.scss).
            VTabs: {
                showArrows: true,
            },
        },
        theme: {
            defaultTheme,
            themes: {
                light: {
                    colors: {
                        ...defaultLightTheme,
                        ...lightTheme,
                    },
                },
                dark: {
                    colors: {
                        ...defaultDarkTheme,
                        ...darkTheme,
                    },
                },
            },
        },
    })

    // `locale` above only seeds the initial value; switching language afterwards
    // has to push into Vuetify explicitly.
    watch(getActiveLocale, (code) => {
        vuetify.locale.current.value = code
    })

    return vuetify
}
